import React, { Fragment, useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import LoadingBackdrop from '../../../LoadingBackdrop/LoadingBackdrop';
import ChatGPTPresetDrawer from '../ChatGPTPresetDrawer';
import { Grid, Typography, Box } from '@mui/material';
import { connect } from 'react-redux';
import { toggleControl } from '../../../../store/control/duck';
import { withRouter } from 'react-router';
import { decreaseGPTQuota, handleError, resetGPTQuota, validateGPTQuota } from '../../../../utils/openai';
import { toast } from 'react-toastify';
import { useForceUpdate } from '../../../../utils/hooks';
import IconToast from '../../../NewToast';
import { trackDirect } from '../../../../store/analytics/api';
import { tracking } from '../../../../utils/tracking';
import { updateFactSheet } from '../../../../store/client/duck';
import { ClientLifeStageOptionPicker } from '../Inputs/Inputs';
import SuggestionTypeSwitchButton from './SuggestionTypeSwitchButton';
import DISCPicker from './Component/DISCPicker';
import { suggestionTypeOptions } from './constant';
import { DISCMapping } from '../Inputs/constant';
import { makeStyles } from 'tss-react/mui';
import Person from '@mui/icons-material/Person';
import { NewsMatchingView } from './View/NewsMatchingView';
import { TopicSuggestionView } from './View/TopicSuggestionView';
import { getNewsRecommendation } from '../../../../store/openai/apis/news-recommendation';
import { openaiGenerateWithTextStreaming } from '../../../../store/openai/api';
import { isClientDisconnected } from '../../utils';
import moment from 'moment';

const useStyles = makeStyles()(() => ({
  // Sections are spaced by the drawer's top-level Grid now, not by a margin
  // each row carries. Kept as an empty hook because five call sites and the
  // two views pass it through as `classes.sectionContainer`.
  sectionContainer: {},
  checkboxOption: {
    paddingTop: '2px',
    paddingBottom: '2px'
  },
  radioOption: {
    paddingTop: '2px',
    paddingBottom: '2px'
  },
  // `primaryActionButton` lived here and carried only borderRadius and width,
  // so this drawer's two CTAs were the only ones in the eight missing
  // ActionButton's padding and `textTransform: 'none'` — visible in English as
  // the one drawer whose generate button shouted in capitals. Both call sites
  // now use ActionButton itself; the class had no other user.
  switchToggleContainer: {
    display: 'flex',
    alignItems: 'center'
  }
}));

const ChatGPTIceBreakingDrawerV2 = props => {
  const {
    intl,
    history,
    chatGPTIceBreakingDrawer,
    toggleControl,
    userDetails,
    // teamCampaignCodes,
    // team,
    updateFactSheet
  } = props;
  const [loading, setLoading] = useState(false);
  const { client, closed, onDrawerClosed } = chatGPTIceBreakingDrawer || {};
  const isOpen = !!chatGPTIceBreakingDrawer && !closed;

  const { classes } = useStyles();
  const forceUpdate = useForceUpdate();
  const [suggestionType, setSuggestionType] = useState('topic');
  const [options, setOptions] = useState(history.location.state?.options || {});
  const [iceBreakingScript, setIceBreakingScript] = useState();
  const [recommendedNews, setRecommendedNews] = useState();

  const toggleTrait = trait => {
    setOptions(prev => {
      const prevTraits = prev.traits || {};
      const newTraits = { ...prevTraits };

      if (trait === 'outgoing' || trait === 'introverted') {
        newTraits.outgoing = false;
        newTraits.introverted = false;
      }
      if (trait === 'logical' || trait === 'emotional') {
        newTraits.logical = false;
        newTraits.emotional = false;
      }

      newTraits[trait] = !prevTraits[trait];

      return { ...prev, traits: newTraits, personality: getPersonality(newTraits) };
    });
  };

  const getPersonality = traits => {
    if (traits.outgoing && traits.logical) return 'D';
    else if (traits.outgoing && traits.emotional) return 'I';
    else if (traits.introverted && traits.emotional) return 'S';
    else if (traits.introverted && traits.logical) return 'C';
    return '';
  };

  useEffect(() => {
    const personality = client?.factsheetId?.personality;
    const traits = (personality && DISCMapping[personality]) || {
      outgoing: false,
      introverted: false,
      logical: false,
      emotional: false
    };
    setOptions(prev => ({
      ...prev,
      personality,
      traits,
      ...(client?.factsheetId?.promptInput ? client?.factsheetId?.promptInput : {})
    }));
  }, [client?.factsheetId?.personality, isOpen]);

  // const onAction = (value) => {
  //     console.log('onAction ::: ', value);
  //     setOptions({ ...options, value });
  // }

  const onValueChange = (key, value, target) => {
    setOptions(prev => {
      if (typeof target === 'string') {
        return { ...prev, [key]: value };
      }
      if (Array.isArray(target)) {
        if (target.includes(value)) {
          return { ...prev, [key]: target.filter(item => item !== value) };
        }
        return { ...prev, [key]: [...target, value] };
      }
      if (typeof target === 'boolean') {
        return { ...prev, [key]: value };
      }
      return { ...prev, [key]: value };
    });
  };

  const onClose = () => {
    // clean up the state
    setOptions({});
    setIceBreakingScript(undefined);
    setRecommendedNews(undefined);
    toggleControl('chatGPTIceBreakingDrawer', { ...chatGPTIceBreakingDrawer, closed: true });
  };

  const onExited = () => {
    toggleControl('chatGPTIceBreakingDrawer', false);
  };

  const onFeedback = () => {
    toggleControl('chatGPTFeedbackDialog', {
      client: client,
      extraContent: options.toString(),
      onSubmit: () => {
        resetGPTQuota();
        forceUpdate();
      }
    });
  };

  const completeAction = result => {
    try {
      setOptions(JSON.parse(result.replace('```json', '').replace('```', '')));
    } catch {
      setOptions([]);
    }
  };

  const handleSetIceBreaking = script => {
    setIceBreakingScript(script);
  };

  const generateIceBreakingScript = async () => {
    trackDirect('useAiFeature');
    trackDirect('generateIceBreakingScript');
    tracking('AI: Generate Ice Breaking Script');

    let policyText = `
        Life coverage: HKD ${client.lifeCoverage}
        Critical coverage: HKD ${client.ciCoverage}
        ${
          client.medicalPolicyCounts?.highend || client.medicalPolicyCounts?.others ? 'Have' : 'Do not have'
        } Medical coverage
        ${client.savingPolicyCount ? 'Have' : 'Do not have'} saving insurance (paying monthly premium HKD ${
      client.totalPremiumSaving
    })
        ${client.ilasPolicyCount ? 'Have' : 'Do not have'} investment insurance (paying monthly premium HKD ${
      client.totalWithILAS
    })
      `;
    let promptData = {
      client_importance: client.factsheetId.importance,
      client_tags: client.factsheetId.tags && client.factsheetId.tags.map(tag => tag.text),
      client_latest_meeting_date:
        client.factsheetId.latestMeetingDate && moment(client.factsheetId.latestMeetingDate).format('DD-MMM-YYYY'),
      client_personality: client.factsheetId.personality,
      client_date_of_birth: client.factsheetId.dob && moment(client.factsheetId.dob).format('DD-MMM-YYYY'),
      client_age: client.factsheetId.age,
      client_gender: client.factsheetId.gender,
      client_is_smoker: client.factsheetId.smoker,
      client_birth_place: client.factsheetId.birthPlace,
      client_occupation: client.factsheetId.occupation,
      client_marriage_status: client.factsheetId.marriageStatus,
      client_monthly_income: client.factsheetId.monthlyIncome,
      client_monthly_expense: client.factsheetId.monthlyExpense,
      client_remark: client.factsheetId.remark,
      policyText: isClientDisconnected(client) ? '' : policyText
    };

    const mode = options.creativity;
    console.log('ChatGPTIceBreakingDrawerV2::: mode::: ', mode);

    const promptIdentifier = 'ice_breaking_script_' + mode;
    console.log('ChatGPTIceBreakingDrawer::: promptIdentifier::: ', promptIdentifier);

    await openaiGenerateWithTextStreaming(promptIdentifier, promptData, [], setIceBreakingScript, null, setLoading);
  };

  const onGenerate = async props => {
    try {
      // if (!options.personality) {
      //     toast.info(IconToast('error', intl.formatMessage({ id: 'select-chat-purpose' })), { className: 'new-toast' });
      //     return;
      // }

      if (!options.personality) {
        toast.info(
          IconToast(
            'error',
            intl.formatMessage({ id: 'input-field-required' }, { field: intl.formatMessage({ id: 'Personality' }) })
          ),
          { className: 'new-toast' }
        );
        return;
      }
      if (!options.lifeStage) {
        toast.info(
          IconToast(
            'error',
            intl.formatMessage(
              { id: 'input-field-required' },
              { field: intl.formatMessage({ id: 'clients-life-stage' }) }
            )
          ),
          { className: 'new-toast' }
        );
        return;
      }
      validateGPTQuota();
      setLoading(true);
      trackDirect('useAiFeature');
      if (suggestionType === 'topic') {
        if (!options.creativity) {
          toast.info(
            IconToast(
              'error',
              intl.formatMessage({ id: 'input-field-required' }, { field: intl.formatMessage({ id: 'creativity' }) })
            ),
            { className: 'new-toast' }
          );
          return;
        }
        await generateIceBreakingScript();
      } else {
        const { news } = props;
        const isRegenerate = news ? true : false;

        // userObjectId, clientObjectId, factsheets, tone, dialect, personality, lifeStage, language, emoji, news
        const result = await getNewsRecommendation({
          feature: 'news-recommendation',
          clientObjectId: client._id,
          factsheets: client.factsheetId,
          userObjectId: userDetails._id,
          tone: options.tone,
          dialect: options.dialect,
          personality: options.personality,
          lifeStage: options.lifeStage,
          language: userDetails.language,
          emoji: options.emoji,
          freeText: options.freeText,
          news: isRegenerate
            ? {
                newsId: news.newsId,
                rationale: news.rationale
              }
            : undefined
        });

        if (result.news) {
          if (isRegenerate && result.news.length > 0) {
            const replacement = result.news[0];
            // const next = recommendedNews || []; // original news list
            //     console.log("News Recommendation API: onGenerate ::: replacement = ", replacement);
            const originalNewsId = news.newsId;
            const originalNewsIndex = result.news.findIndex(item => item.newsId === originalNewsId);
            if (originalNewsIndex !== -1) {
              const next = [...recommendedNews]; // original news list
              // replace the recommendedNews with result
              next[originalNewsIndex] = replacement;
              setRecommendedNews(next);
              toast.success(
                IconToast('success', intl.formatMessage({ id: 'gpt-news-recommendation-regenerated-success' })),
                { className: 'new-toast' }
              );
            }
            //     // replaces the target news with the new result
          } else {
            setRecommendedNews(result.news);
          }
        } else {
          toast.info(IconToast('error', intl.formatMessage({ id: 'gpt-news-recommendation-no-not-found' })), {
            className: 'new-toast'
          });
        }
      }

      decreaseGPTQuota();
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const saveToClient = () => {
    if (client) {
      const fsUpdates = client.factsheetId;
      const promptInput = fsUpdates.promptInput || {};
      // promptInput.financialTargets = target;
      // promptInput.financialInterests = interested;
      // promptInput.lifeStage = lifeStage;

      updateFactSheet({
        id: fsUpdates._id,
        key: 'promptInput',
        value: promptInput,
        clientId: client._id
      });
      updateFactSheet({
        id: fsUpdates._id,
        key: 'personality',
        value: options.personality,
        clientId: client._id
      });
    }
  };

  return (
    <Fragment>
      <LoadingBackdrop open={loading} />
      <ChatGPTPresetDrawer
        open={isOpen}
        onClose={onClose}
        onExited={onExited}
        onFeedback={onFeedback}
        title={intl.formatMessage({ id: 'chat-gpt-options-ice-breaking' })}
        subtitle={intl.formatMessage({ id: 'gpt-subtitle-ice-breaking' })}
        showQuota
      >
        <Grid container spacing={2} wrap="nowrap" style={{ minHeight: '100%' }} sx={{ flexDirection: 'column' }}>
          {/* The switch comes FIRST, above the description it changes — same
              order as 短片腳本生成器, which is the only other drawer with one.
              The two options are two different AIs and the paragraph below says
              what each produces, so reading top to bottom used to mean reading
              an instruction before knowing which tool it was for, finding the
              selector underneath, and re-reading. */}
          <Grid
            className={classes.sectionContainer}
            style={{
              marginLeft: 'auto',
              marginRight: 'auto',
              width: 'fit-content'
            }}
          >
            <SuggestionTypeSwitchButton
              options={suggestionTypeOptions}
              current={suggestionType}
              onAction={next => setSuggestionType(next)}
              classes={classes}
            />
          </Grid>

          <Grid>
            <Typography>{intl.formatMessage({ id: 'chat-gpt-ice-breaking-drawer-v2-desc' })}</Typography>
          </Grid>

          <Grid className={classes.sectionContainer}>
            <DISCPicker
              intl={intl}
              traits={options.traits}
              onToggleTrait={toggleTrait}
              required
              personality={options.personality}
              labelText={intl.formatMessage({ id: 'gpt-icebreaking-personality-title-v2' })}
            />
          </Grid>

          <ClientLifeStageOptionPicker intl={intl} options={options} onValueChange={onValueChange} classes={classes} />

          {suggestionType === 'topic' && (
            <TopicSuggestionView
              intl={intl}
              text={iceBreakingScript}
              disabled={!options.personality}
              onValueChange={onValueChange}
              options={options}
              onGenerate={onGenerate}
              classes={classes}
              onClear={() => {
                setIceBreakingScript();
              }}
            />
          )}
          {suggestionType === 'news' && (
            <NewsMatchingView
              intl={intl}
              news={recommendedNews}
              options={options}
              onValueChange={onValueChange}
              onGenerate={onGenerate}
              classes={classes}
            />
          )}
        </Grid>
      </ChatGPTPresetDrawer>
    </Fragment>
  );
};

const container = connect(
  state => ({
    chatGPTIceBreakingDrawer: state.control.chatGPTIceBreakingDrawer,
    userDetails: state.user.userDetails,
    teamCampaignCodes: state.functions.campaignCodes,
    team: state.team.teams
  }),
  {
    toggleControl,
    updateFactSheet
  }
)(ChatGPTIceBreakingDrawerV2);

export default injectIntl(withRouter(container));
